import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, CardContent, IconButton, Modal, Typography } from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import styled from 'styled-components';

/**
 * Modal reutilizable para capturar una firma digital sobre canvas.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title?: string                   título del header
 *  - description?: string             texto explicativo bajo el título
 *  - onSave: (dataUrl) => Promise|void recibe el PNG en data URL al confirmar
 *
 * El canvas se redimensiona al abrir el modal para que la firma siempre
 * empiece limpia y con el tamaño correcto del contenedor (evita que el
 * trazo aparezca desplazado por cambios de viewport).
 */
const SignaturePadModal = ({ open, onClose, onSave, title = 'Firma digital', description }) => {
    const sigPadRef = useRef(null);
    const wrapperRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
        if (!open) return;
        // Reset al abrir.
        const t = setTimeout(() => {
            sigPadRef.current?.clear();
            setIsEmpty(true);
        }, 0);
        return () => clearTimeout(t);
    }, [open]);

    const handleClose = () => {
        if (submitting) return;
        sigPadRef.current?.clear();
        setIsEmpty(true);
        onClose?.();
    };

    const handleClear = () => {
        sigPadRef.current?.clear();
        setIsEmpty(true);
    };

    const handleEnd = () => {
        // SignatureCanvas dispara onEnd al soltar el trazo. Sincronizamos
        // el flag para habilitar el botón de guardar.
        setIsEmpty(!!sigPadRef.current?.isEmpty());
    };

    const handleSave = async () => {
        if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
            Report.warning('Firma vacía', 'Dibuja tu firma antes de guardar.', 'Okay', { textAlign: 'center' });
            return;
        }
        const dataUrl = sigPadRef.current.toDataURL('image/png');
        try {
            setSubmitting(true);
            await onSave?.(dataUrl);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 560 }}>
                    <CardContent>
                        <Header>
                            <Typography variant="h6">{title}</Typography>
                            <IconButton onClick={handleClose} size="small" disabled={submitting}>
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        {description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {description}
                            </Typography>
                        )}

                        <CanvasWrapper ref={wrapperRef}>
                            <SignatureCanvas
                                ref={sigPadRef}
                                penColor="#1f2937"
                                backgroundColor="rgba(255,255,255,1)"
                                onEnd={handleEnd}
                                canvasProps={{
                                    width: 520,
                                    height: 220,
                                    style: { width: '100%', height: 220, display: 'block' },
                                }}
                            />
                        </CanvasWrapper>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Dibuja tu firma con el mouse o el dedo dentro del recuadro.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Button
                                color="inherit"
                                onClick={handleClear}
                                disabled={submitting || isEmpty}
                                startIcon={<RestartAltIcon />}
                            >
                                Limpiar
                            </Button>
                            <Button color="inherit" onClick={handleClose} disabled={submitting}>
                                Cancelar
                            </Button>
                            <LoadingButton
                                variant="contained"
                                loading={submitting}
                                disabled={isEmpty}
                                onClick={handleSave}
                                startIcon={<CreateIcon />}
                            >
                                <span>Guardar firma</span>
                            </LoadingButton>
                        </Box>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default SignaturePadModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 560px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
`;

const CanvasWrapper = styled(Box)`
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
    touch-action: none;
`;
